import { convertToRaw, EditorState } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import i18next from 'i18next'
import React, { useRef } from 'react'
import { InView } from 'react-intersection-observer'
import {
  ActionMeta,
  GroupBase,
  MultiValue,
  OptionsOrGroups,
  SingleValue,
} from 'react-select'
import {
  ButtonGhost,
  ButtonPrimary,
  ButtonTransparent,
} from '../../../../utils/buttons'
import {
  FormControls,
  FormNotification,
  getFSClassName,
} from '../../../../utils/forms'
import { IngredientsList } from '../../../../utils/ingredients'
import { blockInviewHandler } from '../../../../utils/inview'
import { RichTextEditor } from '../../../../utils/rich-text-editor'
import { SVGIcon, SVGIconType } from '../../../../utils/svgicon'
import { capitalize } from '../../../../utils/utils'
import { authOptions } from '../../rootWidget'

export const ComponentEditLayout = {
  Wrapper: (props: { children: React.ReactNode }) => (
    <InView
      as="section"
      threshold={0}
      triggerOnce={true}
      onChange={(isInView, entry) => blockInviewHandler(isInView, entry.target)}
      className="block block--component-edit"
    >
      <div className="block__inner">{props.children}</div>
    </InView>
  ),
  Expander: (props: {
    title: string
    isTitleField?: boolean
    isOptional?: boolean
    children: React.ReactNode
    preview: string | React.ReactNode
    onCloseDetails?: () => void
  }) => {
    const {
      title,
      children,
      isOptional,
      isTitleField,
      preview,
      onCloseDetails,
    } = props
    return (
      <li className="list__item">
        <details
          className="list__expand"
          open={isTitleField ? true : false}
          onToggle={(e) => {
            if (
              onCloseDetails !== undefined &&
              e.currentTarget instanceof HTMLDetailsElement &&
              !e.currentTarget.open
            )
              onCloseDetails()
          }}
        >
          <summary className="list__header">
            <div className="list__header-inner">
              <div className="list__text">
                <h2 className="list__title title--xs">{title}</h2>
                {!isOptional && (
                  <span className="list__label">
                    {i18next.t('auth:mandatory')}
                  </span>
                )}
              </div>

              <SVGIcon
                className="list__icon list__icon--closed"
                iconId="edit"
                size={20}
              />
              <SVGIcon
                className="list__icon list__icon--opened"
                iconId="checkmark"
                size={24}
              />
            </div>

            {typeof preview == 'string' ? (
              <div
                className="list__preview text--s text--styled"
                dangerouslySetInnerHTML={{ __html: preview }}
              />
            ) : (
              <div className="list__preview text--s">{preview}</div>
            )}
          </summary>
          <div className="list__body">{children}</div>
        </details>
      </li>
    )
  },
  IngredientEditSection: (props: {
    ingredientCount: number
    isMoveUpDisabled: boolean
    isMoveDownDisabled: boolean
    onMoveUp: (_?: HTMLSpanElement) => void
    onMoveDown: (_?: HTMLSpanElement) => void
    onRemove: () => void
    children: React.ReactNode
  }) => {
    const ref = useRef<HTMLSpanElement>(null)
    return (
      <li className="ingredient">
        <span className="ingredient__anchor" ref={ref} aria-hidden="true" />
        <article className="ingredient__wrapper">
          <div
            className="ingredient__header"
            aria-labelledby={`a11y-ingredient-header-${props.ingredientCount}`}
          >
            <h3 className="ingredient__text text--s weight--bold">
              {i18next.t('componentEdit:ingredientCount', {
                count: props.ingredientCount,
              })}
            </h3>
            <div className="ingredient__actions">
              <ComponentEditLayout.IngredientEditAction
                kind="up"
                title={i18next.t('componentEdit:moveUpIngredient')}
                disabled={props.isMoveUpDisabled}
                onClick={() => props.onMoveUp(ref.current || undefined)}
              />
              <ComponentEditLayout.IngredientEditAction
                kind="down"
                title={i18next.t('componentEdit:moveDownIngredient')}
                disabled={props.isMoveDownDisabled}
                onClick={() => props.onMoveDown(ref.current || undefined)}
              />
              <ComponentEditLayout.IngredientEditAction
                kind="delete"
                title={i18next.t('componentEdit:removeIngredient')}
                onClick={props.onRemove}
              />
            </div>
          </div>

          <div className="ingredient__body">{props.children}</div>
        </article>
      </li>
    )
  },
  IngredientEditAction: (props: {
    kind: 'up' | 'down' | 'delete'
    title: string
    disabled?: boolean
    onClick: () => void
  }) => {
    const iconId: SVGIconType = props.kind == 'delete' ? 'delete' : 'arrow'
    const iconSize = props.kind == 'delete' ? 20 : 15
    return (
      <button
        type="button"
        className={`ingredient__action ingredient__action--${props.kind}`}
        {...props}
      >
        <span className="sr-text">{props.title}</span>
        <SVGIcon iconId={iconId} size={iconSize} />
      </button>
    )
  },
  CancelEditedComponent: () => (
    <p>{i18next.t('componentEdit:modalTextCancelEditedComponent')}</p>
  ),
  DeleteEditedComponent: () => (
    <p>{i18next.t('componentEdit:modalTextDeleteEditedComponent')}</p>
  ),
  ComponentFields: (props: {
    paywalled: boolean
    name: { value: string; onChange: (_: string) => void }
    introduction: { value: EditorState; onChange: (_: EditorState) => void }
    preparation: { value: EditorState; onChange: (_: EditorState) => void }
    servingSuggestions: {
      value: EditorState
      onChange: (_: EditorState) => void
    }
    mainIngredients: {
      selected: { id: number; translation: string }[]
      onSearchTerm: (
        term: string,
        callback: (
          options: OptionsOrGroups<
            { value: number; label: string },
            GroupBase<{ value: number; label: string }>
          >
        ) => void
      ) => void
      onChangeValue: (
        newValue: MultiValue<{ value: number; label: string }>,
        actionMeta: ActionMeta<{ value: number; label: string }>
      ) => void
    }
    ingredients: {
      id: number
      amount: { value: string; onChange: (_: string) => void }
      prepreparations: {
        selected: { id: number; translation: string }[]
        options: { id: number; translation: string; onChange: () => void }[]
      }
      ingredient: {
        selected: { id: number; translation: string }
        onSearchTerm: (
          term: string,
          callback: (
            options: OptionsOrGroups<
              { value: number; label: string },
              GroupBase<{ value: number; label: string }>
            >
          ) => void
        ) => void
        onChangeValue: (
          newValue: SingleValue<{ value: number; label: string }>,
          actionMeta: ActionMeta<{ value: number; label: string }>
        ) => void
      }
      unitOfMeasurement: {
        selected: { id: number; translation: string }
        options: { id: number; translation: string }[]
        onChange: (_: string) => void
      }
      onRemove: () => void
      onMoveUp: (_?: HTMLSpanElement) => void
      onMoveDown: (_?: HTMLSpanElement) => void
    }[]
    onAddIngredient: () => void
    worldFlavours: {
      selected: { id: number; translation: string }[]
      options: { id: number; translation: string; onChange: () => void }[]
    }
    techniques: {
      selected: { id: number; translation: string }[]
      options: { id: number; translation: string; onChange: () => void }[]
    }
    appearances: {
      selected: { id: number; translation: string }[]
      options: { id: number; translation: string; onChange: () => void }[]
    }
    textures: {
      selected: { id: number; translation: string }[]
      options: { id: number; translation: string; onChange: () => void }[]
    }
  }) => {
    const {
      paywalled,
      name,
      introduction,
      preparation,
      servingSuggestions,
      mainIngredients,
      ingredients,
      onAddIngredient,
      worldFlavours,
      techniques,
      appearances,
      textures,
    } = props

    return (
      <div className="block__content container--s gutter--m">
        <ul className="block__list list--edit-sections">
          {/* ----- Title */}
          <ComponentEditLayout.Expander
            title={i18next.t('componentEdit:title')}
            preview={name.value}
            isTitleField
          >
            <FormControls.FormItemInGroup
              type="text"
              controlId="component-edit-title"
              hideLabel
              label={i18next.t('componentEdit:title')}
              required={true}
              value={name.value}
              placeholder={i18next.t('componentEdit:titlePlaceholder')}
              onChange={name.onChange}
              maxLength={120}
            />
          </ComponentEditLayout.Expander>

          {/* ----- Introduction (WYSIWYG) */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('componentEdit:description')}
              preview={draftToHtml(
                convertToRaw(introduction.value.getCurrentContent())
              )}
              isOptional
            >
              <RichTextEditor
                editorState={introduction.value}
                onEditorStateChange={introduction.onChange}
              />
            </ComponentEditLayout.Expander>
          )}

          {/* ----- Main Ingredients */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('componentEdit:mainIngredient')}
              preview={getPreview(
                mainIngredients.selected.map((s) => s.translation)
              )}
            >
              <FormControls.FormAsyncSelectInGroup<
                { value: number; label: string },
                true
              >
                label={i18next.t('componentEdit:selectIngredient')}
                controlId={'component-edit-main-ingredient-search'}
                required={true}
                value={mainIngredients.selected.map((s) => ({
                  value: s.id,
                  label: s.translation,
                }))}
                loadOptions={mainIngredients.onSearchTerm}
                onChange={mainIngredients.onChangeValue}
                isMulti
              />
            </ComponentEditLayout.Expander>
          )}

          {/* ----- Ingredients */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('component:ingredients')}
              preview={
                <IngredientsList
                  ingredients={ingredients.map((ingredient) => ({
                    id: ingredient.id,
                    ingredient: ingredient.ingredient.selected.translation,
                    amount: ingredient.amount.value,
                    prepreparations: ingredient.prepreparations.selected.map(
                      (p) => p.translation
                    ),
                    unitOfMeasurement:
                      ingredient.unitOfMeasurement.selected.translation,
                  }))}
                />
              }
            >
              <ul className="list__ingredients list--ingredients">
                {ingredients.map((item, index) => {
                  const {
                    amount,
                    ingredient,
                    onMoveDown,
                    onMoveUp,
                    onRemove,
                    prepreparations,
                    unitOfMeasurement,
                  } = item
                  return (
                    <ComponentEditLayout.IngredientEditSection
                      key={item.id}
                      ingredientCount={index + 1}
                      isMoveUpDisabled={index == 0}
                      isMoveDownDisabled={index + 1 == ingredients.length}
                      onMoveUp={onMoveUp}
                      onMoveDown={onMoveDown}
                      onRemove={onRemove}
                    >
                      {/* Ingredient - Ingredient */}
                      <FormControls.FormAsyncSelectInGroup<
                        { value: number; label: string },
                        false
                      >
                        label={i18next.t('componentEdit:selectIngredient')}
                        controlId={`ingredient-${index}-search`}
                        required={true}
                        value={{
                          value: ingredient.selected.id,
                          label: ingredient.selected.translation,
                        }}
                        loadOptions={ingredient.onSearchTerm}
                        onChange={ingredient.onChangeValue}
                      />

                      {/* Ingredient - Pre-prep */}
                      {prepreparations.options.length > 0 && (
                        <details
                          className={
                            getFSClassName('formGroupClassName') +
                            ' list__preprep-expand'
                          }
                        >
                          <summary
                            className={getFSClassName('formLabelClassName')}
                          >
                            <span>
                              {i18next.t('componentEdit:preparation')}
                              <FormControls.OptionalLabel />
                            </span>

                            <SVGIcon iconId="chevron-down" size={10} />
                          </summary>

                          <div className="list__preprep-body">
                            {prepreparations.options.map((p) => (
                              <FormControls.FormCheckInGroup
                                key={p.id}
                                controlId={`ingredient-${index}-preparation-${p.id}`}
                                label={p.translation}
                                required={false}
                                checked={prepreparations.selected
                                  .map((s) => s.id)
                                  .includes(p.id)}
                                onChange={p.onChange}
                              />
                            ))}
                          </div>
                        </details>
                      )}

                      {/* Ingredient -  Quantity */}
                      <FormControls.FormItemInGroup
                        controlId={`ingredient-${index}-quantity`}
                        inputMode="decimal"
                        label={i18next.t('componentEdit:quantity')}
                        required={false}
                        value={amount.value}
                        onChange={amount.onChange}
                      />

                      {/* Ingredient - Unit */}
                      <FormControls.FormSelectInGroup
                        controlId={`ingredient-${index}-unit`}
                        label={i18next.t('componentEdit:unit')}
                        required={false}
                        value={unitOfMeasurement.selected.id.toString()}
                        onChange={unitOfMeasurement.onChange}
                      >
                        {[
                          <option key={0} value="0">
                            {i18next.t('auth:selectPlaceholder', {
                              entity: i18next
                                .t('componentEdit:unit')
                                .toLocaleLowerCase(),
                            })}
                          </option>,
                          ...unitOfMeasurement.options.map((u) => (
                            <option key={u.id} value={u.id.toString()}>
                              {u.translation}
                            </option>
                          )),
                        ]}
                      </FormControls.FormSelectInGroup>
                    </ComponentEditLayout.IngredientEditSection>
                  )
                })}
              </ul>

              <ButtonGhost
                kind="button"
                size="s"
                label={i18next.t('componentEdit:addIngredient')}
                extraClasses="button--block"
                onClick={onAddIngredient}
              />
            </ComponentEditLayout.Expander>
          )}

          {/* ----- Preparation (WYSIWYG) */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('component:preparationMethod')}
              preview={draftToHtml(
                convertToRaw(preparation.value.getCurrentContent())
              )}
            >
              <RichTextEditor
                editorState={preparation.value}
                onEditorStateChange={preparation.onChange}
              />
            </ComponentEditLayout.Expander>
          )}

          {/* ----- Serving Suggestions (WYSIWYG) */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('component:servingSuggestions')}
              preview={draftToHtml(
                convertToRaw(servingSuggestions.value.getCurrentContent())
              )}
              isOptional
            >
              <RichTextEditor
                editorState={servingSuggestions.value}
                onEditorStateChange={servingSuggestions.onChange}
              />
            </ComponentEditLayout.Expander>
          )}

          {/* ----- Techniques */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('componentFilter:techniques')}
              preview={getPreview(
                techniques.selected.map((s) => s.translation)
              )}
              isOptional
            >
              <fieldset>
                <legend className="sr-text">
                  {i18next.t('componentFilter:techniques')}
                </legend>
                {techniques.options.map((technique) => (
                  <FormControls.FormCheckInGroup
                    key={technique.id}
                    controlId={`tech-${technique.id}`}
                    label={technique.translation}
                    required={false}
                    checked={techniques.selected
                      .map((t) => t.id)
                      .includes(technique.id)}
                    onChange={technique.onChange}
                  />
                ))}
              </fieldset>
            </ComponentEditLayout.Expander>
          )}

          {/* ----- Appearances */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('componentFilter:appearances')}
              preview={getPreview(
                appearances.selected.map((s) => s.translation)
              )}
              isOptional
            >
              <fieldset>
                <legend className="sr-text">
                  {i18next.t('componentFilter:appearances')}
                </legend>
                {appearances.options.map((appearance) => (
                  <FormControls.FormCheckInGroup
                    key={appearance.id}
                    controlId={`ap-${appearance.id}`}
                    label={capitalize(appearance.translation)}
                    required={false}
                    checked={appearances.selected
                      .map((t) => t.id)
                      .includes(appearance.id)}
                    onChange={appearance.onChange}
                  />
                ))}
              </fieldset>
            </ComponentEditLayout.Expander>
          )}

          {/* ----- Textures */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('componentFilter:textures')}
              preview={getPreview(textures.selected.map((s) => s.translation))}
              isOptional
            >
              <fieldset>
                <legend className="sr-text">
                  {i18next.t('componentFilter:textures')}
                </legend>
                {textures.options.map((texture) => (
                  <FormControls.FormCheckInGroup
                    key={texture.id}
                    controlId={`tt-${texture.id}`}
                    label={capitalize(texture.translation)}
                    required={false}
                    checked={textures.selected
                      .map((t) => t.id)
                      .includes(texture.id)}
                    onChange={texture.onChange}
                  />
                ))}
              </fieldset>
            </ComponentEditLayout.Expander>
          )}

          {/* ----- World Flavours */}
          {!paywalled && (
            <ComponentEditLayout.Expander
              title={i18next.t('knowledge:worldFlavoursTitle')}
              preview={getPreview(
                worldFlavours.selected.map((s) => s.translation)
              )}
              isOptional
            >
              <fieldset>
                <legend className="sr-text">
                  {i18next.t('knowledge:worldFlavoursTitle')}
                </legend>
                {worldFlavours.options.map((worldFlavour) => (
                  <FormControls.FormCheckInGroup
                    key={worldFlavour.id}
                    controlId={`wf-${worldFlavour.id}`}
                    label={worldFlavour.translation}
                    required={false}
                    checked={worldFlavours.selected
                      .map((w) => w.id)
                      .includes(worldFlavour.id)}
                    onChange={worldFlavour.onChange}
                  />
                ))}
              </fieldset>
            </ComponentEditLayout.Expander>
          )}
        </ul>
      </div>
    )
  },
  Footer: (props: {
    saveAction: { onClick: () => void; asDraft: boolean }
    onCancel: () => void
    onDelete?: () => void
  }) => (
    <footer className="block__footer">
      <div className="container--s gutter--m">
        {props.saveAction.asDraft && (
          <FormNotification
            extraClasses="block__alert"
            options={authOptions}
            message={i18next.t('componentEdit:saveAsDraftText')}
          />
        )}
        <div
          className={`block__actions${
            props.saveAction.asDraft ? ' block__actions--draft' : ''
          }`}
        >
          <div className="block__action block__action--save">
            {/* TODO NAOMI: styling of save as draft text here:
            {props.saveAction.asDraft &&
              i18next.t('componentEdit:saveAsDraftText')} */}
            <ButtonPrimary
              kind="button"
              type="submit"
              size="s"
              label={
                props.saveAction.asDraft
                  ? i18next.t('componentEdit:saveAsDraftButton')
                  : i18next.t('common:save')
              }
              onClick={props.saveAction.onClick}
            />
          </div>
          <div className="block__action block__action--cancel">
            <ButtonGhost
              kind="button"
              type="button"
              size="s"
              label={i18next.t('common:cancel')}
              onClick={props.onCancel}
            />
          </div>
          {props.onDelete && (
            <div className="block__action block__action--delete">
              <ButtonTransparent
                kind="button"
                type="button"
                size="s"
                label={i18next.t('common:delete')}
                iconId="delete"
                iconSize="20"
                onClick={props.onDelete}
              />
            </div>
          )}
        </div>
      </div>
    </footer>
  ),
}

const getPreview = (values: string[]) =>
  values.length > 0
    ? values.filter((worldFlavour) => worldFlavour).join(', ')
    : undefined
